import {
  POST,
  PUT,
  SITE_LOCATION_TYPE_SYSTEM,
  SITE_LOCATION_TYPE_CODE,
  GROUP_SOURCE_TAG_SYSTEM,
  LEAD_GENERATION_GROUP_SOURCE_TAG,
} from "config/constants/adapter_constants";
import { getTenantFromToken } from "config/helpers/common_helper";
import i18n from "multilingual";
import { toast } from "react-toastify";
import { auditPayload } from "./audithelpers";
import HTTPRequest, {
  FHIRDelete,
  FHIRSave,
  FHIRSearch,
  HTTPAidaBaseService,
  HTTPAgentService,
  NokiEndpointService,
  OrieService,
} from "./http";
import { isEmpty } from "lodash";
import { getHolidayListFromConfig } from "utils/holidayUtils";

export const GET_PATIENT = async (activePersonId) => {
  try {
    let res = await FHIRSearch(`Patient/${activePersonId}`);
    if (res?.data?.total > 0) {
      return res.data.entry[0];
    } else {
      toast.info(i18n.t("common.PATIENT_DATA_NOT_FOUND"));
    }
  } catch (err) {
    toast.error(i18n.t("common.ERROR"));
  }
};

export const UPDATE_PATIENT = async (payload) => {
  try {
    let response = await FHIRSave(PUT, payload);
    return response;
  } catch (err) {
    toast.error(i18n.t("common.ERROR_UPDATING_PATIENT"));
  }
};

export const GET_LOCATIONS = async (query) => {
  try {
    // Only clinical-trial sites — matches the Location.type stamped on create
    // (SITE_LOCATION_TYPE) and on CTMS-synced sites. This also excludes the
    // internal virtual location, which carries a different type/code.
    let siteTypeFilter = `type=${SITE_LOCATION_TYPE_SYSTEM}|${SITE_LOCATION_TYPE_CODE}`;
    let res = await FHIRSearch(`Location?${siteTypeFilter}${query}`);
    // An empty result is not an error — return it and let the caller decide
    // whether "no locations" is worth surfacing to the user. This helper runs
    // during silent app init, where a toast would leak to every page load.
    return res;
  } catch (error) {
    const issue = error?.response?.data?.issue?.[0];
    const message =
      issue?.diagnostics || "Something went wrong. Please try again.";

    // Show toast
    toast.error(message);
  }
};

export const GET_LOCATION_BY_ID = async (id) => {
  try {
    let res = await FHIRSearch(`Location/${id}`);
    if (res?.data?.total > 0) {
      return res?.data?.entry?.[0];
    }
  } catch (err) {
    toast.error(i18n.t("common.ERROR"));
  }
};

export const GET_GROUP_BY_ID = async (id) => {
  try {
    const res = await FHIRSearch(`Group/${id}`);
    const data = res?.data;
    if (data?.resourceType === "Group") return data;
    return data?.entry?.[0]?.resource || null;
  } catch (err) {
    console.error("GET_GROUP_BY_ID error", err);
    return null;
  }
};

export const CREATE_PRACTITIONER = async (payload) => {
  try {
    let res = await FHIRSave(POST, payload);
    return res;
  } catch (err) {
    toast.error(i18n.t("common.ERROR"));
  }
};

export const SAVE_USER_SETTINGS = async (method, payload) => {
  try {
    let res = await FHIRSave(method, payload);
    return res;
  } catch (err) {
    toast.error(i18n.t("common.ERROR"));
  }
};

export const GET_USER_SETTINGS = async (personId) => {
  try {
    let response = await FHIRSearch(
      `?resourceType=Basic&code=user.preference.ui&subject=Person/${personId}`
    );
    return response;
  } catch (err) {
    toast.error(i18n.t("common.ERROR"));
  }
};

export const SAVE_TEMPLATE_PREFERENCES = async (method, payload) => {
  try {
    let res = await FHIRSave(method, payload);
    return res;
  } catch (err) {
    toast.error(i18n.t("common.ERROR"));
  }
};

export const SAVE_DEFAULT_TEMPLATE_PREFERENCES = async (method, payload) => {
  try {
    let res = await FHIRSave(method, payload);
    return res;
  } catch (err) {
    toast.error(i18n.t("common.ERROR"));
  }
};

export const DELETE_VISIT = async (compositionId) => {
  try {
    let response = await FHIRDelete("Composition", compositionId);
    return response;
  } catch (error) {
    console.log(error.message);
  }
};

export const GET_VISITS_BY_KEYWORD = async ({ patientId, searchKeyword }) => {
  try {
    let response = await FHIRSearch(
      `?resourceType=Composition&subject=${patientId}&title:contains=${searchKeyword}&status=preliminary,amended,final`
    );
    return response;
  } catch (err) {
    toast.error(i18n.t("common.ERROR"));
  }
};

export const CREATE_ENCOUNTER = async (payload) => {
  try {
    let response = await FHIRSave(POST, payload);
    return response;
  } catch (e) {
    toast.error(i18n.t("common.ERROR"));
  }
};

export const CREATE_TASK = async (payload) => {
  try {
    let response = await FHIRSave(POST, payload);
    return response;
  } catch (e) {
    toast.error(i18n.t("common.ERROR"));
  }
};

export const GET_TASK_BY_ID = async (taskId) => {
  try {
    let res = await FHIRSearch(`Task/${taskId}`);
    if (res?.data?.total > 0) {
      return res.data.entry[0];
    } else {
      toast.info("Task not found");
    }
  } catch (err) {
    toast.error(i18n.t("common.ERROR"));
  }
};

export const UPDATE_COMPOSITION = async (payload) => {
  try {
    let response = await FHIRSave(PUT, payload);
    return response;
  } catch (e) {
    toast.error(i18n.t("common.ERROR"));
  }
};

export const CREATE_OBSERVATION = async (payload) => {
  try {
    let response = await FHIRSave(POST, payload);
    return response;
  } catch (e) {
    toast.error(i18n.t("common.ERROR"));
  }
};

export const CREATE_COMPOSITION = async (payload) => {
  try {
    let response = await FHIRSave(POST, payload);
    return response;
  } catch (e) {
    toast.error(i18n.t("common.ERROR"));
  }
};

export const CREATE_PATIENT = async (payload) => {
  try {
    let response = await FHIRSave(POST, payload);
    return response;
  } catch (e) {
    toast.error(i18n.t("common.ERROR"));
  }
};

export const UPDATE_CLINICAL_IMPRESSION = async (payload) => {
  try {
    let response = await FHIRSave(PUT, payload);
    return response;
  } catch (e) {
    toast.error(i18n.t("common.ERROR"));
  }
};

export const GET_COMPOSITION = async (encounterId, { patientId = "" } = {}) => {
  try {
    let response = await FHIRSearch(
      `?resourceType=Composition&encounter=${encounterId}&status=preliminary,amended,final`
    );
    return response;
  } catch (err) {
    toast.error(i18n.t("common.ERROR"));
  }
};

export const GET_CLINICAL_IMPRESSION = async (
  encounterId,
  { patientId = "" } = {}
) => {
  try {
    let response = await FHIRSearch(
      `?resourceType=Observation&encounter=${encounterId}&_sort=date`
    );
    return response;
  } catch (err) {
    toast.error(i18n.t("common.ERROR"));
  }
};

export const GET_OBSERVATON_BY_ID = async (id) => {
  try {
    let response = await FHIRSearch(`/Observation/${id}`);
    return response?.data?.entry?.[0];
  } catch (err) {
    console.log("e");
  }
};

export const GET_PROMPT_LIST = async () => {
  try {
    const response = await HTTPRequest.get(
      `/ai-copilot/v1/${getTenantFromToken()}/prompt/list`
    );
    return response?.data;
  } catch (error) {
    console.error(error.message);
  }
};

export const GET_ICD_CPT_CODES = async (payload) => {
  try {
    let response = await HTTPAidaBaseService.post(
      `/${getTenantFromToken()}/generate-codes`,
      {
        ...payload,
      }
    );
    return response;
  } catch (error) {
    console.error(error.message);
    return error;
  }
};

export const EXECUTE_PROMPT = async ({
  prompt_code = "",
  language = "English",
  transcript = "",
  previous_soap_note = "",
  patient = {},
  patient_details = {},
  soap_note = "",
  practitioner = {},
  currentDate = "",
  signal,
  requireLanguageAndConversation = true,
  source_language,
  target_language,
}) => {
  let payload = {
    params: {},
  };
  if (requireLanguageAndConversation) {
    payload.params = {
      language: language,
      conversation: transcript,
    };
  }
  if (source_language) {
    payload.params.source_language = source_language;
  }
  if (target_language) {
    payload.params.target_language = target_language;
  }
  if (!isEmpty(patient)) {
    payload.params.patient = { ...patient };
  }
  if (!isEmpty(patient_details)) {
    payload.params.patient_details = { ...patient_details };
  }
  if (!isEmpty(practitioner)) {
    payload.params["referring_doctor"] = { ...practitioner };
  }
  if (!isEmpty(currentDate) && currentDate.length > 0) {
    payload.params["current_date"] = currentDate;
  }

  if (previous_soap_note) {
    payload.params.previous_soap_note = previous_soap_note;
  }
  if (soap_note) {
    payload.params.soap_note = soap_note;
  }
  try {
    const response = await HTTPAidaBaseService.post(
      `/${getTenantFromToken()}/submit/${prompt_code}`,
      payload,
      { signal: signal }
    );

    return response;
  } catch (error) {
    toast.error(error?.response?.data?.message || error?.message);
    console.error(error.message);
    return false;
  }
};

export const GET_PLANS_LIST = async () => {
  try {
    const response = await HTTPAidaBaseService.get(`/billing/plan-prices`);

    return response.data;
  } catch (error) {
    toast.error(error?.message);
    console.error(error.message);
    return false;
  }
};

export const GET_CUSTOMER_SUBSCRIPTION = async () => {
  try {
    const response = await HTTPAidaBaseService.post(
      `/billing/${getTenantFromToken()}/subscriptions/search`
    );

    return response.data;
  } catch (error) {
    toast.error(error?.message);
    console.error(error.message);
    return false;
  }
};

export const EXECUTE_PROMPT_GENERIC = async (prompt_code, promptParams) => {
  try {
    let payload = {
      params: promptParams,
    };
    const response = await HTTPAidaBaseService.post(
      `/${getTenantFromToken()}/submit/${prompt_code}`,
      payload
    );

    return response.data;
  } catch (error) {
    toast.error(error?.message);
    console.error(error.message);
    return false;
  }
};

export const CREATE_AUDIT_EVENT = async (props) => {
  const payload = auditPayload(
    props?.practitionerID,
    props?.patientName,
    props?.type,
    props?.reason
  );
  try {
    let res = await FHIRSave(POST, payload);
    if (res?.data) {
      return res;
    }
  } catch (e) {
    console.log(e);
  }
};

// Uploads a file to the tenant's GCS bucket via a signed PUT URL and returns
// the stored object path + content type — the `documentInfo` shape the bulk
// upload API expects. The signed URL is minted with content-type
// application/x-www-form-urlencoded (ehr-api-service getSingleFileUploadUrl),
// so the PUT must send that exact header or the GCS signature check fails.
export const uploadFileToGcs = async (file, { prefix = "uploads" } = {}) => {
  const tenantId = getTenantFromToken();
  const base = (file?.name || "file")
    .replace(/\.[^.]+$/, "")
    .replace(/[^A-Z0-9]+/gi, "_");
  const ext = (file?.name?.match(/\.([^.]+)$/)?.[1] || "csv").toLowerCase();
  const documentPath = `${prefix}/${Date.now()}_${base}.${ext}`;

  const { data: uploadUrl } = await HTTPRequest.get(
    `file/tenants/${tenantId}/getSingleFileUploadUrl?fileName=${documentPath}`
  );

  const putRes = await fetch(uploadUrl, {
    method: "PUT",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: file,
  });
  if (!putRes.ok) {
    throw new Error(`File upload to storage failed (HTTP ${putRes.status})`);
  }

  return { documentPath, contentType: file?.type || "text/csv" };
};

// Bulk upload runs on samaras-api-service (intake-router), so it goes through
// HTTPAgentService (the proxied /api/ai/v1 client), like the campaign flow.
export const CREATE_BULK_PATIENTS = async (payload, signal) => {
  try {
    const res = await HTTPAgentService.post(
      `intake/tenant/${getTenantFromToken()}/patient-bulk-upload`,
      payload,
      signal ? { signal } : undefined
    );
    return res?.data;
  } catch (err) {
    console.error("Error starting bulk upload:", err);
    throw err.response?.data || err.message || "Upload failed";
  }
};

// Single-patient pool upload — same samaras-api-service client as the bulk flow.
export const CREATE_SINGLE_PATIENTS = async (payload) => {
  try {
    const res = await HTTPAgentService.post(
      `intake/tenant/${getTenantFromToken()}/patient-create-single`,
      payload
    );
    return res?.data;
  } catch (err) {
    console.error("Error creating single patient:", err);
    throw err.response?.data || err.message || "Create failed";
  }
};

export const CREATE_SCHEDULE_COMPOSITION = async (payload) => {
  try {
    const res = await NokiEndpointService({
      endpoint: `/intake/tenants/${getTenantFromToken()}/schedule-plan`,
      action: "POST",
      payload,
    });

    return res?.data;
  } catch (err) {
    console.error("Error uploading CSV:", err);
    throw err.response?.data || err.message || "Upload failed";
  }
};

export const GET_RELATED_PERSON = async (relatedPersonId) => {
  try {
    const res = await FHIRSearch(`RelatedPerson/${relatedPersonId}`);
    return res?.data;
  } catch (err) {
    console.error("Error fetching related person:", err);
    throw err.response?.data || err.message || "Fetch failed";
  }
};

export const GET_COMPOSITIONS_BY_QUERY = async ({ queryParams = "" }) => {
  try {
    const res = await FHIRSearch(`Composition?${queryParams}`);
    return res;
  } catch (error) {
    console.error("Error fetching compositions by task IDs:", error);
    throw error;
  }
};

export const getAllComp = async ({ ids = "" }) => {
  try {
    const res = await FHIRSearch(`Composition`, `_id=${ids}`);
    return res;
  } catch (error) {
    console.error("Error fetching all compositions:", error);
    throw error;
  }
};

export const MOVE_TO_ONBOARDING = async (payload) => {
  const res = await OrieService(POST, `/assistants/tools/book_appointment`, {
    ...payload,
    language: "en",
    tenant_id: getTenantFromToken(),
  });
  return res?.data;
};

export const GET_CALENDAR_HOLIDAYS_CONFIG = async ({ organisationId = "" }) => {
  if (!organisationId) return;
  try {
    const response = await FHIRSearch(
      `?resourceType=Basic&code=organization.preference.calendar&subject=Organization/${organisationId}`
    );

    if (response?.data?.total) {
      const rawResource = {
        ...response?.data?.entry?.[0]?.resource,
      };
      const configString = rawResource?.extension?.find(
        (item) => item?.url === "http://enliv.com/calendar/config"
      )?.valueString;

      if (configString) {
        const config = JSON.parse(configString);
        const list = getHolidayListFromConfig(config);
        return Array.isArray(list) ? list : [];
      } else {
        return [];
      }
    } else {
      return [];
    }
  } catch (e) {
    console.error("Failed to load calendar holidays", e);
    return [];
  }
};

export const GET_QUESTIONNAIRE = async (query = "") => {
  try {
    const response = await FHIRSearch(`Questionnaire${query}`);
    return response.data;
  } catch (e) {
    console.log(e);
  }
};

export const createResource = async (payload) => {
  try {
    const response = await FHIRSave(POST, payload);
    return response;
  } catch (error) {
    console.error(`Error creating ${payload?.resourceType}`, error);
  }
};

export const updateResource = async (payload) => {
  try {
    const response = await FHIRSave(PUT, payload);
    return response;
  } catch (error) {
    console.error(`Error updating ${payload?.resourceType}`, error);
    return Promise.reject({
      code: 500,
      message: `Failed to update ${payload?.resourceType}`,
      error,
    });
  }
};

export const getResource = async (formType, patientId, encounterId) => {
  let response = null;
  try {
    const url = `QuestionnaireResponse?subject=${patientId}&Questionnaire=Questionnaire/${formType}&encounter=${encounterId}`;
    const res = await FHIRSearch(url);
    if (res?.data) {
      response = res.data;
    }
  } catch (e) {
    console.error(`EHR->services->getResource error: ${e}`);
  }
  return response;
};

export const RESEARCH_STUDY_TAG_SYSTEM =
  "http://enliv.com/fhir/questionnaire/tag/research-study-id";

// Searchable category for questionnaires, stored in meta.tag. We use a tag
// (not Questionnaire.code) because this server indexes _tag search but not the
// `code` search parameter. "research-study-questionnaire" is the first
// category; add more values here as new questionnaire types are introduced.
export const QUESTIONNAIRE_CATEGORY_TAG_SYSTEM =
  "http://enliv.com/fhir/questionnaire/tag/category";
export const QUESTIONNAIRE_CATEGORY_RESEARCH_STUDY =
  "research-study-questionnaire";
export const QUESTIONNAIRE_CATEGORY_RESEARCH_STUDY_DISPLAY =
  "Research Study Questionnaire";

export const GET_RESEARCH_STUDIES = async (query = "") => {
  try {
    const res = await FHIRSearch(`ResearchStudy?${query}`);
    return res?.data;
  } catch (err) {
    console.error("GET_RESEARCH_STUDIES error", err);
    const issue = err?.response?.data?.issue?.[0];
    toast.error(issue?.diagnostics || "Failed to load studies");
    return null;
  }
};

export const GET_RESEARCH_STUDY_BY_ID = async (id) => {
  try {
    const res = await FHIRSearch(`ResearchStudy/${id}`);
    return res?.data;
  } catch (err) {
    console.error("GET_RESEARCH_STUDY_BY_ID error", err);
    toast.error(i18n.t("common.ERROR"));
    return null;
  }
};

export const GET_QUESTIONNAIRES_BY_STUDY = async (studyId, extraQuery = "") => {
  try {
    // Filter by study id AND category, both stored in meta.tag. Repeated _tag
    // params are AND-ed per the FHIR spec. We use tags (not Questionnaire.code)
    // because this server indexes _tag search but not the `code` parameter.
    const studyTag = `${RESEARCH_STUDY_TAG_SYSTEM}|${studyId}`;
    const categoryTag = `${QUESTIONNAIRE_CATEGORY_TAG_SYSTEM}|${QUESTIONNAIRE_CATEGORY_RESEARCH_STUDY}`;
    const res = await FHIRSearch(
      `Questionnaire?_tag=${encodeURIComponent(
        studyTag
      )}&_tag=${encodeURIComponent(categoryTag)}${extraQuery}`
    );
    return res?.data;
  } catch (err) {
    console.error("GET_QUESTIONNAIRES_BY_STUDY error", err);
    return null;
  }
};

export const GET_RESEARCH_SUBJECTS_BY_STUDY = async (
  studyId,
  extraQuery = ""
) => {
  try {
    // _include pulls the Patient resource referenced by each ResearchSubject
    // into the same bundle so the list can render names without N+1 fetches.
    const res = await FHIRSearch(
      `ResearchSubject?study=ResearchStudy/${encodeURIComponent(
        studyId
      )}&_include=ResearchSubject:individual${extraQuery}`
    );
    return res?.data;
  } catch (err) {
    console.error("GET_RESEARCH_SUBJECTS_BY_STUDY error", err);
    return null;
  }
};

export const GET_RESEARCH_SUBJECTS_BY_PATIENT = async (
  patientId,
  extraQuery = ""
) => {
  try {
    const res = await FHIRSearch(
      `ResearchSubject?individual=Patient/${encodeURIComponent(
        patientId
      )}&_include=ResearchSubject:study${extraQuery}`
    );
    return res?.data;
  } catch (err) {
    console.error("GET_RESEARCH_SUBJECTS_BY_PATIENT error", err);
    return null;
  }
};

export const GET_QUESTIONNAIRES_BY_URL = async (url) => {
  try {
    const res = await FHIRSearch(
      `Questionnaire?url=${encodeURIComponent(url)}`
    );
    return res?.data;
  } catch (err) {
    console.error("GET_QUESTIONNAIRES_BY_URL error", err);
    return null;
  }
};

export const GET_QUESTIONNAIRES_BY_IDENTIFIER = async (system, value) => {
  try {
    const token = `${system}|${value}`;
    const res = await FHIRSearch(
      `Questionnaire?identifier=${encodeURIComponent(token)}`
    );
    return res?.data;
  } catch (err) {
    console.error("GET_QUESTIONNAIRES_BY_IDENTIFIER error", err);
    return null;
  }
};

export const DELETE_RESEARCH_STUDY = async (id) => {
  try {
    const res = await FHIRDelete("ResearchStudy", id);
    return res;
  } catch (err) {
    console.error("DELETE_RESEARCH_STUDY error", err);
    toast.error("Failed to delete study");
    return null;
  }
};

// NOTE: there is deliberately no `name` filter. The FHIR store does not index
// `Group.name`, so `name`/`name:contains` is silently ignored here (and 400s under
// `Prefer: handling=strict`) — passing it returned EVERY pool while looking like a
// search, which made callers believe they had filtered. Filter client-side with
// `filterPoolsByName` over the pools you have loaded.
export const GET_SUBJECT_POOLS = async ({
  count = 10,
  pageToken = "",
} = {}) => {
  try {
    // Exclude lead-generation "Ad Leads" Groups (created during campaign launch)
    // from the subject-pool pickers. They carry a group-source meta tag; the FHIR
    // store supports the _tag:not modifier (see OnboardedList patient-source filter).
    let params = `code=subject-pool&_tag:not=${encodeURIComponent(
      GROUP_SOURCE_TAG_SYSTEM
    )}|${encodeURIComponent(LEAD_GENERATION_GROUP_SOURCE_TAG)}&_count=${count}`;
    if (pageToken) params += `&_page_token=${encodeURIComponent(pageToken)}`;
    const res = await FHIRSearch(`Group`, params);
    return res?.data || null;
  } catch (err) {
    console.error("GET_SUBJECT_POOLS error", err);
    return null;
  }
};

export const DELETE_QUESTIONNAIRE = async (id) => {
  try {
    const res = await FHIRDelete("Questionnaire", id);
    return res;
  } catch (err) {
    console.error("DELETE_QUESTIONNAIRE error", err);
    toast.error("Failed to delete questionnaire");
    return null;
  }
};
